/** One product's page for a bar: the e-shop's product URL, or Testuj.to's reviewed page. null = no usable URL. */
export interface ProductLink {
    productId: string;
    productName: string;
    url: string | null;
}

export interface EshopBar {
    key: string;
    name: string;
    count: number;
    /** One entry per product carrying the destination; empty for a delivered-but-unconfigured destination. */
    links: ProductLink[];
}

/**
 * How a bar's links render (TTV2-1559 R8, A8):
 *   - single: every product carrying the destination has the same URL → the portal name is the link;
 *   - perProduct: the products' URLs differ, or only some have one → "<product> ↗" per product, and a
 *     product without a URL as its plain name + "Nebyl dodán link";
 *   - none: no product has a URL (or no product carries it) → plain name + "Nebyl dodán link".
 */
export type BarLinkForm =
    | { kind: "single"; url: string }
    | { kind: "perProduct"; links: ProductLink[] }
    | { kind: "none" };

export const barLinkForm = (links: ProductLink[]): BarLinkForm => {
    const urls = links.map((link) => link.url);
    if (urls.length === 0 || urls.every((url) => !url)) {
        return { kind: "none" };
    }
    if (urls.every((url) => url && url === urls[0])) {
        return { kind: "single", url: urls[0] as string };
    }
    return { kind: "perProduct", links };
};

type DestinationLike = {
    type?: string | null;
    key?: string | null;
    name?: string | null;
    config?: string | null;
    configFields?: Array<{ type?: string | null; path?: string | null } | null> | null;
} | null;

type ProductReviewDestinationsLike =
    | Array<{
          productId?: string | null;
          destinations?: Array<DestinationLike> | null;
      } | null>
    | null
    | undefined;

type ProductReviewTranslationDestinationsLike =
    | Array<{
          productId?: string | null;
          tenantId?: string | null;
          destinations?: Array<DestinationLike> | null;
      } | null>
    | null
    | undefined;

type DeliveriesLike =
    | Array<{
          destination?: string | null;
          reviewId?: string | null;
          lang?: string | null;
          origin?: string | null;
      } | null>
    | null
    | undefined;

type ReviewsLike = Array<{ submittedContents?: Array<string> | null } | null> | null | undefined;

// A delivered copy's destination is `${type}/${key || ""}` (admin-api approveReview/06_update.js:77),
// so a destination stored with key null is delivered under "<type>/".
const destinationKeyOf = (destination: NonNullable<DestinationLike>) => `${destination.type}/${destination.key || ""}`;

type ProductsLike = Array<{ id?: string | null; name?: string | null; slug?: string | null } | null> | null | undefined;

// A translation tenant's review language, as the admin SPA resolves it when it creates the
// translated copy (tt-v2-admin-frontend/src/utils/lang.ts resolveLang: cz → cs, …). Copied, not
// shared: there are no shared types across components.
const LANG_BY_TENANT: Record<string, string> = {
    gb: "en",
    us: "en",
    cz: "cs",
    sk: "sk",
    de: "de",
    pl: "pl",
    hu: "hu",
    fr: "fr",
    ro: "ro",
    bg: "bg",
    lt: "lt",
    rs: "sr",
    hr: "hr",
    si: "sl",
    it: "it",
    es: "es",
};

const TRANSLATION_ORIGIN = "translation";

/** Only an absolute http(s) URL is rendered as a link (A5); anything else counts as missing. */
export const httpUrl = (value: unknown): string | null => {
    if (typeof value !== "string") {
        return null;
    }
    const trimmed = value.trim();
    if (!/^https?:\/\//i.test(trimmed)) {
        return null;
    }
    try {
        const parsed = new URL(trimmed);
        return parsed.protocol === "http:" || parsed.protocol === "https:" ? trimmed : null;
    } catch {
        return null;
    }
};

const parseConfig = (config: unknown): Record<string, unknown> => {
    if (!config) {
        return {};
    }
    if (typeof config === "object") {
        return config as Record<string, unknown>;
    }
    if (typeof config !== "string") {
        return {};
    }
    try {
        const parsed = JSON.parse(config);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
};

// R7: an integrated destination keeps its product URL at the path of its url-type configField
// (productUrl on every catalogue destination today), falling back to config.productUrl; a custom
// destination keeps it at config.productUrl (the admin's "URL produktu"). A link inside a custom
// destination's instructions is not a product URL.
const productUrlOf = (destination: NonNullable<DestinationLike>): string | null => {
    const config = parseConfig(destination.config);
    if (destination.type !== "custom") {
        const urlField = (destination.configFields || []).find((field) => field?.type === "url" && field.path);
        const fromField = urlField ? httpUrl(config[urlField.path as string]) : null;
        if (fromField) {
            return fromField;
        }
    }
    return httpUrl(config.productUrl);
};

const productNames = (products: ProductsLike) => {
    const names = new Map<string, string>();
    (products || []).forEach((product) => {
        if (product?.id) {
            names.set(product.id, product.name || product.id);
        }
    });
    return names;
};

/** Adds a product's URL to a bar's links, once per product (a product listed twice keeps its first URL). */
const addLink = (links: ProductLink[], link: ProductLink) => {
    const existing = links.find((entry) => entry.productId === link.productId);
    if (!existing) {
        links.push(link);
    } else if (!existing.url && link.url) {
        existing.url = link.url;
    }
};

interface ConfiguredBar {
    key: string;
    destination: string; // "<type>/<key>", as in Campaign.reviewDeliveries
    name: string;
    isCustom: boolean;
    links: ProductLink[];
}

// Per-destination review counts and product links for the distribution chart. One bar per
// configured destination × (main | translation tenant) — the main destinations and every
// translation destination, the internal Testuj.to ("tt") one excluded as it is the funnel's own
// row — plus any delivered destination the campaign does not configure (raw type as label). A custom
// destination shows even with 0 submissions, so its link or "Nebyl dodán link" is visible; an
// integrated one shows only when something was delivered (A1, revised).
//
// Counts, by the correct signal:
//   - integrated destinations (heureka, drmax, idealo, notino, planeo, …) -> Campaign.reviewDeliveries
//     (copies actually shipped there, isShipped), distinct reviews per bar. A copy belongs to a
//     translation bar when its origin is "translation" and its lang is that translation tenant's
//     language (A3); everything else — an SK reviewer's own review on a CZ campaign included —
//     stays in the main bar (R4);
//   - custom destinations (type === "custom", no push/pull integration) -> reviews whose
//     submittedContents includes the destination.
// The discriminator is integration-vs-none, NOT the confirmSubmission checkbox: isShipped is never
// set for a custom source (nothing ships to it), so its only delivery signal is that a review was
// submitted there — whether that submission was gated by the "recenze vložena" checkbox
// (confirmSubmission) or by required review text (requireContent) or neither. Keying on
// confirmSubmission dropped custom destinations that require text instead of the checkbox (e.g. BENU.cz).
export const deriveEshopBarsHybrid = (
    productReviewDestinations: ProductReviewDestinationsLike,
    productReviewTranslationDestinations: ProductReviewTranslationDestinationsLike,
    deliveries: DeliveriesLike,
    reviews: ReviewsLike,
    products: ProductsLike
): EshopBar[] => {
    const names = productNames(products);
    const linkFor = (productId: string | null | undefined, destination: NonNullable<DestinationLike>) =>
        productId
            ? { productId, productName: names.get(productId) || productId, url: productUrlOf(destination) }
            : null;

    const configured = new Map<string, ConfiguredBar>();
    const configure = (
        barKey: string,
        destination: NonNullable<DestinationLike>,
        label: string,
        link: ProductLink | null
    ) => {
        let bar = configured.get(barKey);
        if (!bar) {
            bar = {
                key: barKey,
                destination: destinationKeyOf(destination),
                name: label,
                isCustom: destination.type === "custom",
                links: [],
            };
            configured.set(barKey, bar);
        }
        if (link) {
            addLink(bar.links, link);
        }
    };

    (productReviewDestinations || []).forEach((entry) => {
        (entry?.destinations || []).forEach((destination) => {
            if (!destination?.type || destination.type === "tt") {
                return;
            }
            const key = destinationKeyOf(destination);
            configure(key, destination, destination.name || destination.type, linkFor(entry?.productId, destination));
        });
    });

    // Translation bars, keyed "<type>/<key>@<tenant>"; labelled "<name> (<TENANT>)" (A2).
    const translationBarByCopy = new Map<string, string>(); // "<type>/<key>|<lang>" -> bar key
    (productReviewTranslationDestinations || []).forEach((entry) => {
        const tenantId = entry?.tenantId;
        if (!tenantId) {
            return;
        }
        (entry?.destinations || []).forEach((destination) => {
            if (!destination?.type || destination.type === "tt") {
                return;
            }
            const destinationKey = destinationKeyOf(destination);
            const barKey = `${destinationKey}@${tenantId}`;
            const label = `${destination.name || destination.type} (${tenantId.toUpperCase()})`;
            configure(barKey, destination, label, linkFor(entry?.productId, destination));
            const lang = LANG_BY_TENANT[tenantId];
            if (lang) {
                translationBarByCopy.set(`${destinationKey}|${lang}`, barKey);
            }
        });
    });

    // Distinct reviews shipped to each bar.
    const deliveredByBar = new Map<string, Set<string>>();
    (deliveries || []).forEach((delivery) => {
        const destination = delivery?.destination;
        if (!destination || !delivery?.reviewId || destination.startsWith("tt/")) {
            return;
        }
        const translationBar =
            delivery.origin === TRANSLATION_ORIGIN && delivery.lang
                ? translationBarByCopy.get(`${destination}|${delivery.lang}`)
                : undefined;
        const barKey = translationBar || destination;
        if (!deliveredByBar.has(barKey)) {
            deliveredByBar.set(barKey, new Set());
        }
        deliveredByBar.get(barKey)?.add(delivery.reviewId);
    });

    // Distinct reviews submitted to each destination (the delivery signal for custom sources).
    const submittedByKey = new Map<string, number>();
    (reviews || []).forEach((review) => {
        const seen = new Set<string>();
        (review?.submittedContents || []).forEach((entry) => {
            if (!entry || seen.has(entry)) {
                return;
            }
            seen.add(entry);
            submittedByKey.set(entry, (submittedByKey.get(entry) || 0) + 1);
        });
    });

    // A1 (revised): a custom destination keeps its bar at 0 (it is configured and its link is worth
    // seeing); an integrated one, main or translation, gets a bar only once something was delivered.
    const bars: EshopBar[] = [...configured.values()]
        .map((bar) => ({
            key: bar.key,
            name: bar.name,
            count: bar.isCustom ? submittedByKey.get(bar.destination) || 0 : deliveredByBar.get(bar.key)?.size || 0,
            links: bar.links,
            isCustom: bar.isCustom,
        }))
        .filter((bar) => bar.isCustom || bar.count > 0)
        .map(({ key, name, count, links }) => ({ key, name, count, links }));

    // Delivered to a destination the campaign does not configure: today's fallback bar, no URL.
    deliveredByBar.forEach((reviewIds, barKey) => {
        if (configured.has(barKey)) {
            return;
        }
        bars.push({ key: barKey, name: barKey.split("/")[0], count: reviewIds.size, links: [] });
    });

    return bars.sort((a, b) => b.count - a.count);
};

/**
 * The Testuj.to row's links (A4, A9): each product's reviewed page on the main tenant's domain,
 * https://<domainName>/reviewed/<slug>; a missing domain or slug leaves that product without a URL.
 * Only products whose destinations include `tt` are listed: a product published nowhere on
 * Testuj.to has no reviewed page to link. An empty list means the campaign never used the channel,
 * so the row shows no "missing link" note (A9b).
 */
export const deriveTestujtoLinks = (
    products: ProductsLike,
    domainName: string | null | undefined,
    productReviewDestinations: ProductReviewDestinationsLike
): ProductLink[] => {
    const domain = (domainName || "").trim();
    const carryingTt = new Set(
        (productReviewDestinations || [])
            .filter((entry) => (entry?.destinations || []).some((destination) => destination?.type === "tt"))
            .map((entry) => entry?.productId)
    );
    return (products || []).flatMap((product) => {
        if (!product?.id || !carryingTt.has(product.id)) {
            return [];
        }
        const url =
            domain && product.slug ? httpUrl(`https://${domain}/reviewed/${encodeURIComponent(product.slug)}`) : null;
        return [{ productId: product.id, productName: product.name || product.id, url }];
    });
};
